// Silent op handlers for the turn-trigger + on_enter_location pipeline.
//
// "Silent" means the op fires inline without surfacing a Confirm prompt
// to the player. These are ops whose card text describes automatic
// resolution: "destroy this site", "submerge it", "dispel <self>".
// A confirm step would be pure friction.
//
// Pure-ish. Every side effect routes through the `host` adapter so
// this module owns NO references to GameBoard. The adapter contract is
// kept narrow (see `Host` doc below) so adding a new silent op = add a
// pure handler + a case in `dispatchSilentOp`, no GameBoard edits.
//
// /**
//  * @typedef {Object} Host
//  * @property {(id: string) => boolean} meshesHas
//  *   Is this card currently on the table (i.e. has a live mesh)?
//  *   Used to guard ops against already-removed sources after an
//  *   earlier effect in the same entry cleared the card.
//  * @property {(key: string, values?: object) => void} logAction
//  * @property {(card: object) => void} sendToCemetery
//  * @property {(card: object) => void} sendToHand. Routes to the card's
//  *   OWNER's hand, not the local player's (the trigger may fire on the
//  *   opponent's client when they move a minion into the aura).
//  * @property {(card: object) => void} turnSiteToRubble
//  * @property {(card: object, region: 'underground'|'underwater'|null) => void} forceSubsurface
//  *   Survival-keyword + lethality already handled by GameBoard's impl.
//  *   When passed a carried card (tagged `_carrierId` by resolveScope),
//  *   GameBoard's adapter detaches it to the field first so the mesh-
//  *   based primitive can act on it.
//  * @property {(sourceCard: object, scope: object, args: object) => object[]} resolveScope
//  * @property {(card: object, amount: number, opts: object) => void} damageTarget
//  * @property {(sourceCard: object, controller: string, op: string, args: object) => void} iterateEachOp
//  */
//
// Adding a new silent op:
//   1. Add the OP constant in `schema.js`.
//   2. Add a small `applyXxx` handler here, taking (sourceCard, ...args, host).
//   3. Add a `case OP.XXX` in `dispatchSilentOp`.
//   4. If the op uses a new host primitive, extend the Host contract.
// GameBoard never grows new dispatch code. Its host adapter just
// gains a method if a new primitive is needed.

import { OP } from '../schema';
import { msg } from '../../../logMessage';
import { isForceMovable } from '../forcedMovement';
import { snapshotRealm } from './cards/kairosArchive';
import { bindTurnLifespan } from '../../core/auraDuration';
import { markCellFired } from '../../core/firstVisit';
import { applyDamageRandomInScope } from './scopeDamage';
import { applySummonTokenAtCovered } from './coveredSummon';
import { applyKillByPowerInScope } from './scopeKillByPower';
import { applyDamageBearerPerArtifactInScope, applyDestroyEquippedInScope } from './auraArtifactRestrictions';
import { applyMoveAuraToUnvisitedAdjacent } from './auraSelfMove';
import { applyAllPlayersDrawPerScope } from './cards/seelieCourt';
import { applyDrawForBearer } from './pickupDraw';
import { applySacrificeSelfDraw } from './surfaceTreasure';

export function applyPlayTopAtlasAtCovered(sourceCard, host) {
  const anchor = sourceCard?._auraAnchor;
  if (!anchor) return;
  const controller = sourceCard.rotated ? 'p2' : 'p1';
  host.playTopAtlasAtCell(controller, { col: anchor.col, row: anchor.row });
}

export function applyUntapSelf(sourceCard, host) {
  if (!host.meshesHas(sourceCard.id)) return;
  host.setUntapped(sourceCard);
}

export function applyDispelSelf(sourceCard, host) {
  if (!host.meshesHas(sourceCard.id)) return;
  host.logAction('{source} dispels', { source: sourceCard.name || msg('Aura') });
  host.sendToCemetery(sourceCard);
}

export function applyConvertEachInScopeToRubble(sourceCard, entry, args, host) {
  const filter = args?.filter || { type: 'Site' };
  const matches = host.resolveScope(sourceCard, entry.scope, { filter, exclude_self: false });
  // The Great Drowning of Men: "flood the resulting Rubble." Same op
  // with an opt-in flag so the Rubble that replaces each destroyed
  // site enters play already carrying the Flooded ability. Combined
  // with a follow-up submerge_each_in_scope in the same entry, this
  // gives the codex behaviour: non-Submerge minions/artifacts atop
  // are force-submerged → die for lacking the survival keyword.
  const flooded = !!args?.flooded;
  for (const t of matches) {
    if (!t?.isSite || t.cardId === 'sorcery-rubble') continue;
    const values = { source: sourceCard.name || msg('Aura'), target: t.name || msg('site') };
    if (flooded) host.logAction('{source} destroys {target}. Becomes Flooded Rubble', values);
    else host.logAction('{source} destroys {target}. Becomes Rubble', values);
    host.turnSiteToRubble(t, { flooded });
  }
}

// Shrine of the Dragonlord: "Genesis → Teleport all Dragons here."
// Iterates every in-play card matching args.filter and moves it to
// the anchor's cell. The anchor is the equipped Monument's HOST site
// when fired from the attach pipeline (opts.anchorCard), or the
// source itself otherwise. `unmovable` cards stay put (Talamh Dreig).
export function applyTeleportEachToSource(sourceCard, entry, args, host, opts = {}) {
  const destCell = host.cellOfCard(opts.anchorCard || sourceCard);
  if (!destCell) {
    host.logAction('{source}: no location to teleport to', { source: sourceCard.name || msg('Source') });
    return;
  }
  const filter = args?.filter || {};
  const matches = host.resolveScope(sourceCard, { where: 'global' }, { filter, exclude_self: true });
  for (const t of matches) {
    if (!isForceMovable(t)) {
      host.logAction("{target} can't be moved by force", { target: t.name || msg('A unit') });
      continue;
    }
    const cur = host.cellOfCard(t);
    if (cur && cur.col === destCell.col && cur.row === destCell.row) continue;
    host.moveCardToCell(t, destCell, sourceCard.name || 'Source');
  }
}

// Kairos the Archivist Genesis. Stamps the realm snapshot on the
// source and asks the host to replicate it to the peer (the Deathrite
// can fire on either client). FAQ: "there is only a single archive of
// the realm at a time." A fresh archive becomes the snapshot for
// EVERY Kairos deathrite, so every other in-play Kairos copy is
// stamped with the same archive (newest wins).
export function applyArchiveRealmState(sourceCard, host) {
  const meshes = host.getMeshes();
  const cellAt = host.getCellAt();
  const cards = [];
  for (const [, mesh] of meshes) {
    const c = mesh?.userData?.cardInstance;
    if (!c || c.id === sourceCard.id) continue;
    cards.push({ ...c, cell: cellAt(mesh.position.x, mesh.position.z) });
  }
  sourceCard._realmArchive = snapshotRealm(cards);
  host.syncRealmArchive?.(sourceCard);
  for (const [, mesh] of meshes) {
    const c = mesh?.userData?.cardInstance;
    if (!c || c.id === sourceCard.id || c.cardId !== sourceCard.cardId) continue;
    c._realmArchive = sourceCard._realmArchive;
    host.syncRealmArchive?.(c);
  }
  host.logAction('{source} archives the state of the realm', { source: sourceCard.name || msg('Kairos') });
}

export function applySubmergeEachInScope(sourceCard, entry, args, host) {
  // Default filter: every minion-or-artifact in scope. The covered
  // scope only matches by CELL, not region, so an "atop affected sites"
  // submerge must pass `region: 'surface'` in its filter to skip units
  // already burrowed/submerged beneath the site (The Great Drowning).
  const filter = args?.filter || { any_of: [{ type: 'Minion' }, { type: 'Artifact' }] };
  // exclude_self defaults true (an aura source is never a minion/artifact
  // anyway); Vatn Draconis threads exclude_self:false so the source Dragon.
  // itself a minion "here." Dives with the rest ("all minions and artifacts
  // here", the source included).
  const excludeSelf = args?.exclude_self !== false;
  const matches = host.resolveScope(sourceCard, entry.scope, { filter, exclude_self: excludeSelf });
  const matchedIds = new Set(matches.map((t) => t.id));
  for (const t of matches) {
    // A carried card (resolveScope surfaces equipped artifacts at their
    // bearer's square) whose bearer is ALSO submerged here rides along,
    // staying equipped. Skip it. A carried card on a bearer that isn't
    // itself submerged (an Avatar) drops off to submerge alone;
    // forceSubsurface detaches it first via materializeCarried.
    if (t._carrierId && matchedIds.has(t._carrierId)) continue;
    host.logAction('{source} submerges {target}', { source: sourceCard.name || msg('Aura'), target: t.name || msg('a unit') });
    host.forceSubsurface(t, 'underwater');
  }
}

export function applyForceRegionWatchTarget(sourceCard, watchTarget, args, host) {
  if (!watchTarget) return;
  const region = args?.region === 'underground' ? 'underground' : 'underwater';
  const values = { source: sourceCard.name || msg('Aura'), target: watchTarget.name || msg('unit') };
  if (region === 'underground') host.logAction('{source} burrows {target}', values);
  else host.logAction('{source} submerges {target}', values);
  host.forceSubsurface(watchTarget, region);
}

export function applyReturnSelfToHand(sourceCard, host) {
  if (!host.meshesHas(sourceCard.id) && !host.canReturnToHand?.(sourceCard)) return;
  host.logAction('{source} returns to hand', { source: sourceCard.name || msg('Aura') });
  host.sendToHand(sourceCard);
}

export function applyBindTurnLifespan(sourceCard, args) {
  bindTurnLifespan(sourceCard, args?.turns);
}

export function applyGrantKeywordToWatchTarget(sourceCard, watchTarget, args, host) {
  if (!watchTarget) return;
  const keyword = args?.keyword;
  if (!keyword) return;
  host.grantKeywordToCard(watchTarget, keyword);
  host.logAction('{source} grants {keyword} to {target}', { source: sourceCard.name || msg('Source'), keyword, target: watchTarget.name || msg('unit') });
}

export function applyDamageWatchTarget(sourceCard, watchTarget, args, host) {
  if (!watchTarget) return;
  host.damageTarget(watchTarget, args?.amount || 1, {
    sourceLabel: sourceCard.name || 'source',
    log: true,
    sweep: true,
    floatingDamage: true,
  });
}

// Burn the first-time-at-cell flag for the source's current cell.
// Pairs with the read-only `first_visit_to_cell` condition; runs on
// resolution so a declined "may" keeps the first-time intact.
export function applyMarkCellVisited(sourceCard, args, host) {
  const cell = host.cellOfCard(sourceCard);
  if (!cell) return;
  markCellFired(sourceCard, cell, args?.per_cell === true);
}

// Skip the affected player's next turn. The controller resolved for
// this entry is the affected player (for an uncontrolled source like
// Vaults of Zul this is the entering Avatar's controller, threaded in
// via opts.controller). The host primitive stamps that player's avatar
// and broadcasts so the flag reaches the player's own client, where
// their START phase consumes it.
export function applySkipNextTurn(controller, host) {
  if (!controller) return;
  host.skipNextTurnForPlayer(controller);
}

// Sir Perceval Genesis: "An opponent chooses a site." Opens a cell-pick
// for the OPPONENT of the source's controller, constrained to cells
// holding a Site. The opponent's pick stamps `_percevalTargetCell` so
// the on_stop draw can recognise "that site".
export function applyOpenOpponentSiteCellPick(sourceCard, host) {
  if (!host.meshesHas(sourceCard.id)) return;
  const owner = sourceCard.rotated ? 'p2' : 'p1';
  const opponent = owner === 'p1' ? 'p2' : 'p1';
  host.openPercevalSitePick(sourceCard, opponent);
}

// Buried Treasure / Sunken Treasure cast clause: open an OPPONENT
// cell-pick over the controller's own land (Buried) / water (Sunken)
// sites. The opponent's chosen cell conjures the Treasure there
// stamped subsurface. The host owns the geometry (which cells are
// allied land/water sites) and the spawn; this handler only routes
// the element onto the pick.
export function applyConjureUnderSite(sourceCard, args, host) {
  if (!host.meshesHas(sourceCard.id)) return;
  const owner = sourceCard.rotated ? 'p2' : 'p1';
  const opponent = owner === 'p1' ? 'p2' : 'p1';
  const element = args?.element === 'water' ? 'water' : 'land';
  host.openConjureUnderSitePick(sourceCard, opponent, element);
}

// 13 Treasures of Britain on_pickup follow-up: detach the Treasures
// from its bearer and re-spawn it atop a uniformly random board cell.
// The host owns the bearer lookup, the dice (host.rng), and the synced
// place primitive that broadcasts the chosen cell to the opponent.
export function applyTeleportToRandomSiteOrVoidTop(sourceCard, host) {
  host.teleportSelfToRandomSiteOrVoidTop(sourceCard);
}

// Sir Perceval on_stop: gated by `source_at_remembered_cell`. He just
// stopped on the chosen site. Draw a card, then reactivate Genesis by
// re-opening the opponent's pick. The re-arm clears the remembered cell
// first so the gate doesn't instantly re-satisfy on the next stop.
export function applyPercevalDrawRearm(sourceCard, args, host) {
  if (!host.meshesHas(sourceCard.id)) return;
  const owner = sourceCard.rotated ? 'p2' : 'p1';
  const amount = args?.amount ?? 1;
  host.drawSpellsFor(owner, amount, args?.source);
  host.setPercevalTarget(sourceCard, null);
  const opponent = owner === 'p1' ? 'p2' : 'p1';
  host.openPercevalSitePick(sourceCard, opponent);
}

/**
 * Single dispatch entry point. Returns `true` if the op was handled
 * silently; `false` if the op isn't in this module's responsibility
 * (caller should route through the prompt / target-pick pipeline).
 *
 * `opts` carries the firing context. `watchTarget` for on_enter_location
 * / on_damage_dealt style triggers, `controller` for ops that iterate
 * scope on behalf of a specific player.
 */
export function dispatchSilentOp(eff, sourceCard, entry, host, opts = {}) {
  switch (eff.op) {
    case OP.HEAL_FULL:
      host.healFull(opts.watchTarget || sourceCard);
      return true;
    case OP.UNTAP_IN_SCOPE:
      for (const card of host.resolveScope(sourceCard, entry.scope, { filter: eff.args?.filter || {}, exclude_self: false })) host.setUntapped(card);
      return true;
    case OP.DISPEL_SELF:
      applyDispelSelf(sourceCard, host);
      return true;
    case OP.CONVERT_EACH_IN_SCOPE_TO_RUBBLE:
      applyConvertEachInScopeToRubble(sourceCard, entry, eff.args || {}, host);
      return true;
    case OP.SUBMERGE_EACH_IN_SCOPE:
      applySubmergeEachInScope(sourceCard, entry, eff.args || {}, host);
      return true;
    case OP.TELEPORT_EACH_TO_SOURCE:
      applyTeleportEachToSource(sourceCard, entry, eff.args || {}, host, opts);
      return true;
    case OP.ARCHIVE_REALM_STATE:
      applyArchiveRealmState(sourceCard, host);
      return true;
    case OP.FORCE_REGION_WATCH_TARGET:
      applyForceRegionWatchTarget(sourceCard, opts.watchTarget, eff.args || {}, host);
      return true;
    case OP.RETURN_SELF_TO_HAND:
      applyReturnSelfToHand(sourceCard, host);
      return true;
    case OP.DAMAGE_WATCH_TARGET:
      applyDamageWatchTarget(sourceCard, opts.watchTarget, eff.args || {}, host);
      return true;
    case OP.KILL_EACH:
    case OP.DAMAGE_EACH:
    case OP.RETURN_EACH_TO_HAND:
      host.iterateEachOp(sourceCard, opts.controller, eff.op, eff.args || {});
      return true;
    case OP.BIND_TURN_LIFESPAN:
      applyBindTurnLifespan(sourceCard, eff.args || {});
      return true;
    case OP.DAMAGE_RANDOM_IN_SCOPE:
      applyDamageRandomInScope(sourceCard, entry, eff.args || {}, host);
      return true;
    case OP.SUMMON_TOKEN_AT_COVERED:
      applySummonTokenAtCovered(sourceCard, eff.args || {}, host);
      return true;
    case OP.GRANT_KEYWORD_TO_WATCH_TARGET:
      applyGrantKeywordToWatchTarget(sourceCard, opts.watchTarget, eff.args || {}, host);
      return true;
    case OP.KILL_BY_POWER_IN_SCOPE:
      applyKillByPowerInScope(sourceCard, entry, eff.args || {}, host);
      return true;
    case OP.DAMAGE_BEARER_PER_ARTIFACT_IN_SCOPE:
      applyDamageBearerPerArtifactInScope(sourceCard, eff.args || {}, host);
      return true;
    case OP.DESTROY_EQUIPPED_IN_SCOPE:
      applyDestroyEquippedInScope(sourceCard, host);
      return true;
    case OP.MOVE_AURA_TO_UNVISITED_ADJACENT:
      applyMoveAuraToUnvisitedAdjacent(sourceCard, host, opts.controller);
      return true;
    case OP.PLAY_TOP_ATLAS_AT_COVERED:
      applyPlayTopAtlasAtCovered(sourceCard, host);
      return true;
    case OP.MARK_CELL_VISITED:
      applyMarkCellVisited(sourceCard, eff.args || {}, host);
      return true;
    case OP.UNTAP_SELF:
      applyUntapSelf(sourceCard, host);
      return true;
    case OP.SKIP_NEXT_TURN:
      applySkipNextTurn(opts.controller || (sourceCard.rotated ? 'p2' : 'p1'), host);
      return true;
    case OP.ALL_PLAYERS_DRAW_PER_SCOPE:
      applyAllPlayersDrawPerScope(sourceCard, eff.args || {}, host);
      return true;
    case OP.DRAW_FOR_BEARER:
      applyDrawForBearer(sourceCard, eff.args || {}, host,
        opts.controller || (sourceCard.rotated ? 'p2' : 'p1'));
      return true;
    case OP.SACRIFICE_SELF_DRAW:
      applySacrificeSelfDraw(sourceCard, eff.args || {}, host);
      return true;
    case OP.CONJURE_UNDER_SITE:
      applyConjureUnderSite(sourceCard, eff.args || {}, host);
      return true;
    case OP.TELEPORT_TO_RANDOM_SITE_OR_VOID_TOP:
      applyTeleportToRandomSiteOrVoidTop(sourceCard, host);
      return true;
    case OP.OPEN_OPPONENT_SITE_CELL_PICK:
      applyOpenOpponentSiteCellPick(sourceCard, host);
      return true;
    case OP.PERCEVAL_DRAW_REARM:
      applyPercevalDrawRearm(sourceCard, eff.args || {}, host);
      return true;
    default:
      return false;
  }
}

/**
 * If every effect on `entry` is in this module's silent set, dispatch
 * them all and return `true` so the caller knows to skip the prompt
 * pipeline. Returns `false` if any effect requires the prompt path.
 *
 * Used by applyCardAutomation as an early-exit for entries that
 * shouldn't surface a Confirm prompt (e.g. on_play `bind_turn_lifespan`,
 * or future inline-resolve ops on Genesis triggers).
 */
export function tryDispatchAllSilent(entry, sourceCard, host, opts = {}) {
  const effects = entry?.effect || [];
  if (effects.length === 0) return false;
  if (!effects.every((e) => SILENT_OP_SET.has(e.op))) return false;
  for (const eff of effects) {
    dispatchSilentOp(eff, sourceCard, entry, host, opts);
  }
  return true;
}

/**
 * Ops in this module's silent set, for callers that need to gate
 * "should I use the silent path or the prompt path?" decisions BEFORE
 * actually dispatching. Mirrors the cases in `dispatchSilentOp`.
 */
export const SILENT_OP_SET = new Set([
  OP.DISPEL_SELF,
  OP.UNTAP_SELF,
  OP.CONVERT_EACH_IN_SCOPE_TO_RUBBLE,
  OP.SUBMERGE_EACH_IN_SCOPE,
  OP.TELEPORT_EACH_TO_SOURCE,
  OP.ARCHIVE_REALM_STATE,
  OP.FORCE_REGION_WATCH_TARGET,
  OP.RETURN_SELF_TO_HAND,
  OP.UNTAP_IN_SCOPE,
  OP.HEAL_FULL,
  OP.DAMAGE_WATCH_TARGET,
  OP.KILL_EACH,
  OP.DAMAGE_EACH,
  OP.RETURN_EACH_TO_HAND,
  OP.BIND_TURN_LIFESPAN,
  OP.DAMAGE_RANDOM_IN_SCOPE,
  OP.SUMMON_TOKEN_AT_COVERED,
  OP.GRANT_KEYWORD_TO_WATCH_TARGET,
  OP.KILL_BY_POWER_IN_SCOPE,
  OP.DAMAGE_BEARER_PER_ARTIFACT_IN_SCOPE,
  OP.DESTROY_EQUIPPED_IN_SCOPE,
  OP.MOVE_AURA_TO_UNVISITED_ADJACENT,
  OP.PLAY_TOP_ATLAS_AT_COVERED,
  OP.MARK_CELL_VISITED,
  OP.SKIP_NEXT_TURN,
  OP.ALL_PLAYERS_DRAW_PER_SCOPE,
  OP.DRAW_FOR_BEARER,
  OP.SACRIFICE_SELF_DRAW,
  OP.CONJURE_UNDER_SITE,
  OP.TELEPORT_TO_RANDOM_SITE_OR_VOID_TOP,
  OP.OPEN_OPPONENT_SITE_CELL_PICK,
  OP.PERCEVAL_DRAW_REARM,
]);
